import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import styles from './loader.module.css';
import clsx from 'clsx';

export type LoaderProps = React.ComponentPropsWithoutRef<'span'> & {
  'data-theme'?: string;
  radius?: 'lg' | 'md' | 'full';
  // eslint-disable-next-line @typescript-eslint/ban-types
  size?: 'lg' | 'md' | 'sm' | number | (string & {}); // (string & {}) for better IntelliSense - see https://stackoverflow.com/a/61048124;;
  timedContent?: Record<number, React.ReactNode>;
  variant?: 'skeleton' | 'spinner' | 'ellipsis';
};

export const Loader = forwardRef<HTMLSpanElement, LoaderProps>(function Loader(
  {
    children,
    className,
    radius = 'md',
    size = 'md',
    style,
    timedContent,
    variant = 'spinner',
    ...rest
  },
  ref
) {
  const [content, setContent] = useState(children);
  const innerRef = useRef<HTMLSpanElement>(null);
  const height = styles[`size-${size}`] ? undefined : Number(size) || size;
  const label = rest['aria-label'];

  useImperativeHandle(ref, () => innerRef.current as HTMLSpanElement); // Forward innerRef
  useEffect(() => {
    innerRef.current?.style.setProperty('--delay', getDelay()); // Remember delay, and only apply on client to avoid hydration issues
  }, []);

  // Change text after a specified delay
  useEffect(() => {
    setContent(children); // Reset content
    Object.entries(timedContent || {}).forEach(([ms, jsx]) =>
      setTimeout(() => setContent(jsx), Number(ms))
    );

    if (!children && !label && variant !== 'skeleton')
      console.warn('Missing aria-label for <Loader>', innerRef);
  }, [timedContent, children, label, variant]);

  return (
    <span
      className={clsx(
        styles.loader,
        styles[`radius-${radius}`],
        styles[`size-${size}`],
        styles[variant],
        className
      )}
      aria-live="polite"
      aria-busy="true"
      role={!content && label ? 'img' : rest.role}
      style={{ height, ...style }}
      ref={innerRef}
      {...rest}
    >
      {variant === 'spinner' && (
        <svg viewBox="25 25 50 50" aria-hidden="true">
          <circle cx="50" cy="50" r="20" />
          <circle cx="50" cy="50" r="20" />
        </svg>
      )}
      {content}
    </span>
  );
});

// If we render something again and 1 second has passed,
// reset the delayStagger so we do not get huge delay
let delayStagger = 0;
let delayTimer: ReturnType<typeof setTimeout>;
const getDelay = () => {
  clearTimeout(delayTimer);
  delayTimer = setTimeout(() => (delayStagger = 0), 1000);
  return `${delayStagger++ * 100}ms`;
};
